import React, { useState, useEffect, useCallback, useRef } from 'react';
import styles from './Slider.module.css';
import VideoPlayer from './components/VideoPlayer';
import SlideContent from './components/SlideContent';
import { categoryImages } from '../homeData';

const Slider = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [vh, setVh] = useState(window.innerHeight * 0.01);
  const videoRefs = useRef([]);
  const preloadTimeout = useRef(null);
  const transitionTimeout = useRef(null);
  const autoPlayInterval = useRef(null);

  const items = categoryImages;

  const updateVh = useCallback(() => {
    setVh(window.innerHeight * 0.01);
  }, []);

  useEffect(() => {
    updateVh();
    window.addEventListener('resize', updateVh);
    return () => window.removeEventListener('resize', updateVh);
  }, [updateVh]);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera;
      const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      const isMobileDevice = mobileRegex.test(userAgent.toLowerCase()) || window.innerWidth <= 768;
      setIsMobile(isMobileDevice);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleTransition = useCallback((newIndex) => {
    if (isTransitioning) return;
    
    // Clear any existing transition timeout
    if (transitionTimeout.current) {
      clearTimeout(transitionTimeout.current);
    }

    setIsTransitioning(true);
    setIsLoaded(false);
    setActiveIndex(newIndex);
    
    // Use a shorter transition time on mobile
    const transitionTime = isMobile ? 400 : 800;
    transitionTimeout.current = setTimeout(() => setIsTransitioning(false), transitionTime);
  }, [isTransitioning, isMobile]);

  const nextSlide = useCallback(() => {
    handleTransition((activeIndex + 1) % items.length);
  }, [activeIndex, items.length, handleTransition]);

  const prevSlide = useCallback(() => {
    handleTransition((activeIndex - 1 + items.length) % items.length);
  }, [activeIndex, items.length, handleTransition]);

  // Optimized preloading for mobile
  useEffect(() => {
    const preloadVideos = () => {
      const nextIndex = (activeIndex + 1) % items.length;
      const prevIndex = (activeIndex - 1 + items.length) % items.length;

      // On mobile, only preload the next video
      if (isMobile) {
        if (videoRefs.current[nextIndex]) {
          videoRefs.current[nextIndex].current?.load();
        }
      } else {
        // On desktop, preload both next and previous
        if (videoRefs.current[nextIndex]) {
          videoRefs.current[nextIndex].current?.load();
        }
        if (videoRefs.current[prevIndex]) {
          videoRefs.current[prevIndex].current?.load();
        }
      }
    };

    // Clear any existing timeout
    if (preloadTimeout.current) {
      clearTimeout(preloadTimeout.current);
    }

    // Set a new timeout to preload videos
    // Use shorter timeout on mobile
    const preloadDelay = isMobile ? 500 : 1000;
    preloadTimeout.current = setTimeout(preloadVideos, preloadDelay);

    return () => {
      if (preloadTimeout.current) {
        clearTimeout(preloadTimeout.current);
      }
    };
  }, [activeIndex, items.length, isMobile]);

  // Optimized touch handling for mobile
  useEffect(() => {
    const handleTouchStart = (e) => {
      setTouchStart(e.touches[0].clientX);
    };

    const handleTouchMove = (e) => {
      setTouchEnd(e.touches[0].clientX);
    };

    const handleTouchEnd = () => {
      if (!touchStart || !touchEnd) return;
      const distance = touchStart - touchEnd;
      // Use smaller threshold on mobile for more responsive swipes
      const threshold = isMobile ? 30 : 50;
      const isLeftSwipe = distance > threshold;
      const isRightSwipe = distance < -threshold;

      if (isLeftSwipe) {
        nextSlide();
      } else if (isRightSwipe) {
        prevSlide();
      }

      setTouchEnd(null);
      setTouchStart(null);
    };

    const slider = document.querySelector(`.${styles.container}`);
    if (slider) {
      slider.addEventListener('touchstart', handleTouchStart, { passive: true });
      slider.addEventListener('touchmove', handleTouchMove, { passive: true });
      slider.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      if (slider) {
        slider.removeEventListener('touchstart', handleTouchStart);
        slider.removeEventListener('touchmove', handleTouchMove);
        slider.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [touchStart, touchEnd, nextSlide, prevSlide, isMobile]);

  // Optimized autoplay for mobile
  useEffect(() => {
    // Clear any existing interval
    if (autoPlayInterval.current) {
      clearInterval(autoPlayInterval.current);
    }

    // Use longer interval on mobile to save battery
    const interval = setInterval(() => {
      if (!isTransitioning) {
        nextSlide();
      }
    }, isMobile ? 8000 : 5000);

    autoPlayInterval.current = interval;

    return () => {
      if (autoPlayInterval.current) {
        clearInterval(autoPlayInterval.current);
      }
    };
  }, [isTransitioning, nextSlide, isMobile]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (preloadTimeout.current) clearTimeout(preloadTimeout.current);
      if (transitionTimeout.current) clearTimeout(transitionTimeout.current);
      if (autoPlayInterval.current) clearInterval(autoPlayInterval.current);
    };
  }, []);

  return (
    <div 
      className={styles.container}
      style={{ '--vh': `${vh}px` }}
    >
      {items.map((item, index) => {
        const isActive = index === activeIndex;
        const isNext = index === (activeIndex + 1) % items.length;
        const isPrev = index === (activeIndex - 1 + items.length) % items.length;
        // On mobile, only load active and next slide
        const shouldLoad = isMobile ? (isActive || isNext) : (isActive || isNext || isPrev);

        return (
          <div
            key={item.id}
            className={`${styles.slide} ${isActive ? styles.active : ''} ${
              isNext ? styles.next : ''
            } ${isPrev ? styles.prev : ''}`}
          >
            {shouldLoad && (
              <VideoPlayer
                key={item.id + '-' + (isActive ? 'active' : 'inactive')}
                ref={el => videoRefs.current[index] = el}
                src={item.image}
                webm={item.webm}
                mobileSrc={item.mobileImage}
                mobileWebm={item.mobileWebm}
                poster={item.poster}
                isActive={isActive}
                onLoad={() => setIsLoaded(true)}
              />
            )}
            <div className={styles.gradientOverlay} />
            <SlideContent item={item} isLoaded={isLoaded} />
          </div>
        );
      })}

      {/* Hide controls on mobile */}
      {!isMobile && (
        <div className={styles.controls}>
          <button 
            className={`${styles.controlButton} ${styles.prevButton}`}
            onClick={prevSlide}
            aria-label="Previous slide"
            disabled={isTransitioning}
          >
            <span className={styles.arrow}>←</span>
          </button>
          <button 
            className={`${styles.controlButton} ${styles.nextButton}`}
            onClick={nextSlide}
            aria-label="Next slide"
            disabled={isTransitioning}
          >
            <span className={styles.arrow}>→</span>
          </button>
        </div>
      )}

      {/* Finger swipe hint for mobile */}
      {isMobile && (
        <div className={styles.fingerHint}>
          <i className="fas fa-hand-point-left"></i>
        </div>
      )}

      <div className={styles.pagination}>
        {items.map((_, index) => (
          <button
            key={index}
            className={`${styles.paginationDot} ${
              index === activeIndex ? styles.active : ''
            }`}
            onClick={() => {
              if (!isTransitioning) {
                handleTransition(index);
              }
            }}
            aria-label={`Go to slide ${index + 1}`}
            disabled={isTransitioning}
          />
        ))}
      </div>
    </div>
  );
};

export default Slider; 